import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import Sound from 'react-sound';
import { toast } from 'react-toastify';

import pingFile from 'assets/sound/ping.mp3';

import { onReloadPage, updateResultStatus } from 'redux/actions/index';

import APIUtils from './apis/APIUtils';
import Socket from './apis/Socket';
import { useNotification } from './context/NotificationContext';

import 'react-toastify/dist/ReactToastify.css';
import 'common/scss/App.scss';

let intervalIdDeposit = null;
let intervalIdWidthdraw = null;
let intervalIdWidthKycPending = null;
let intervalIdTrackingCloned = null;
let globalCanRepeatSound = false;
const SocketComponent = ({ soundData }) => {
    const [notification, dispatchNotification] = useNotification();
    const [socket, setSocket] = useState(null);
    const [playSoundDeposit, setPlaySoundDeposit] = useState(false);
    const [playSoundWithdraw, setPlaySoundWithdraw] = useState(false);
    const [playSoundKycPending, setPlaySoundKycPending] = useState(false);
    const [playTrackingCloned, setPlayTrackingCloned] = useState(false);

    const { t } = useTranslation();
    const dispatch = useDispatch();

    const handleSoundRepeat = (data) => {
        if (data) {
            if (data.deposit <= 0) {
                clearInterval(intervalIdWidthdraw);
                intervalIdWidthdraw = null;
            }

            if (data.withdraw <= 0) {
                clearInterval(intervalIdDeposit);
                intervalIdDeposit = null;
            }

            if (data.total_pending <= 0) {
                clearInterval(intervalIdWidthKycPending);
                intervalIdWidthKycPending = null;
            }

            if (data.total_notifications <= 0) {
                clearInterval(intervalIdTrackingCloned);
                intervalIdTrackingCloned = null;
            }

            if (data.type === 'pagadw_update_notify' && data.action_type === 'pending') {
                setPlaySoundWithdraw(true);
                if (globalCanRepeatSound && !intervalIdWidthdraw && soundData?.withdraw_sound_repeat === 'repeat') {
                    intervalIdWidthdraw = setInterval(()=>{
                        setPlaySoundWithdraw(true);
                    }, 180000);
                }
            }

            if (data.type === 'kyc_pending' && data.kyc_status === 'kyc_pending') {
                setPlaySoundKycPending(true);
                if (globalCanRepeatSound && !intervalIdWidthKycPending && soundData?.kyc_pending_sound_repeat === 'repeat') {
                    intervalIdWidthKycPending = setInterval(()=>{
                        setPlaySoundKycPending(true);
                    }, 180000);
                }
            }

            if (data.type === 'top_bar_menu_tracking_cloned_notify' && data.total_notifications) {
                setPlayTrackingCloned(true);
                if (globalCanRepeatSound && !intervalIdTrackingCloned && soundData?.tracking_cloned_sound_repeat === 'repeat') {
                    intervalIdTrackingCloned = setInterval(()=>{
                        setPlayTrackingCloned(true);
                    }, 180000);
                }
            }

            if (data.has_new_request) {
                if (data.request_type === 'deposit') {
                    setPlaySoundDeposit(true);
                    if (globalCanRepeatSound && !intervalIdDeposit && soundData?.deposit_sound_repeat === 'repeat') {
                        intervalIdDeposit = setInterval(()=>{
                            setPlaySoundDeposit(true);
                        }, 180000);
                    }
                }

                if (data.request_type === 'withdraw') {
                    setPlaySoundWithdraw(true);
                    if (globalCanRepeatSound && !intervalIdWidthdraw && soundData?.withdraw_sound_repeat === 'repeat') {
                        intervalIdWidthdraw = setInterval(()=>{
                            setPlaySoundWithdraw(true);
                        }, 180000);
                    }
                }
            }
        }
    };

    const setNotification = (data) => {
        handleSoundRepeat(data);
        dispatchNotification(data);
    };

    useEffect(()=> {
        // clear repeat when at request management list
        if (!notification.repeatSound) {
            clearInterval(intervalIdDeposit);
            clearInterval(intervalIdWidthdraw);
            clearInterval(intervalIdWidthKycPending);

            intervalIdWidthdraw = null;
            intervalIdDeposit = null;
            intervalIdWidthKycPending = null;
        }

        globalCanRepeatSound = notification.repeatSound;
    }, [notification.repeatSound]);

    const setResultStatus = (data) => {
        if (data.success) {
            dispatch(updateResultStatus(data.type));
        }
    };

    const handleBackendClientVersion = useCallback((data) => {
        const [localMajor = 0, localMinor = 0, localPatch = 0] = (process.env.REACT_APP_BACKEND_CLIENT_VERSION || '').split('.');
        const [serverMajor = 0, serverMinor = 0, serverPatch = 0] = (data?.version || '').split('.');

        if (Number(localMajor) >= Number(serverMajor) &&
            Number(localMinor) >= Number(serverMinor) &&
            Number(localPatch) >= Number(serverPatch)) {
            return;
        } else {
            dispatch(onReloadPage(true));
        }
    }, [dispatch]);

    const handleMessage = (method, data)=> {
        if (method === 'logout') {
            APIUtils.logOut(data.reason);
            window.location.reload();
        }
        if (method === 'top_bar_menu_dw_notify') {
            setNotification({
                type: 'update_notification',
                ...data,
            });
        } else if (method === 'top_bar_action_log_notify') {
            setNotification({
                type: 'log_need_update',
                ...data,
            });
        } else if (method === 'memo_changed') {
            setNotification({
                type: 'memo_need_update',
                ...data,
            });
        } else if (method === 'top_bar_autobank_notication') {
            setNotification({
                type: 'log_need_update_autobank',
                ...data
            });
        } else if (method === 'top_bar_menu_pagadw_notify') {
            setNotification({
                type: 'pagadw_update_notify',
                ...data,
            });
        } else if (method === 'top_bar_menu_kyc_notify') {
            setNotification({
                type: 'kyc_pending',
                ...data,
            });
        } else if (method === 'toast_message') {
            let message = '';
            if (data && data.type === 'build_app') {
                if (data.success) {
                    message = `${data.app_name} ${t('Build')} ${t('Success')}`;
                } else {
                    message = `${data.app_name} ${t('Build')} ${t('Unsuccess')}`;
                }
            } else {
                if (data && data.message && data.message !== '') {
                    message = t(data.message);
                }
            }

            if (message && message !== '') {
                if (data.success) {
                    toast.success(message);
                } else {
                    toast.error(message);
                }
            }
        } else if (method === 'top_bar_menu_tracking_cloned_notify') {
            setNotification({
                type: 'top_bar_menu_tracking_cloned_notify',
                ...data
            });
        } else if (method === 'top_bar_menu_running_text') {
            setNotification({
                type: 'top_bar_menu_running_text',
                ...data
            });
        }

        if (method === 'period_result_cancelled' || method === 'period_result_updated') {
            setResultStatus({
                type: method,
                ...data,
            });
        }
    };

    useEffect(() => {
        if (APIUtils.isAuthed()) {
            setSocket(new Socket(handleMessage, setNotification, setResultStatus, handleBackendClientVersion));
        }
    }, [handleBackendClientVersion]);

    useEffect(() => {
        if (socket) {
            socket.connect();
        }
    }, [socket]);

    return (<>
        <Sound
            url={soundData?.deposit_sound ? soundData?.deposit_sound : pingFile}
            playStatus={playSoundDeposit ? Sound.status.PLAYING : Sound.status.STOPPED}
            onFinishedPlaying={() => {
                setPlaySoundDeposit(false);
            }}
        />

        <Sound
            url={soundData?.withdraw_sound ? soundData?.withdraw_sound : pingFile}
            playStatus={playSoundWithdraw ? Sound.status.PLAYING : Sound.status.STOPPED}
            onFinishedPlaying={() => {
                setPlaySoundWithdraw(false);
            }}
        />

        <Sound
            url={soundData?.kyc_pending_sound ? soundData?.kyc_pending_sound : pingFile}
            playStatus={playSoundKycPending ? Sound.status.PLAYING : Sound.status.STOPPED}
            onFinishedPlaying={() => {
                setPlaySoundKycPending(false);
            }}
        />

        <Sound
            url={soundData?.tracking_cloned_sound ? soundData?.tracking_cloned_sound : pingFile}
            playStatus={playTrackingCloned ? Sound.status.PLAYING : Sound.status.STOPPED}
            onFinishedPlaying={() => {
                setPlayTrackingCloned(false);
            }}
        />
    </>);
};

export default SocketComponent;
